/* Premium visual modernization — count-up hook.
 *
 * Animates a number from 0 → target with an ease-out curve using
 * requestAnimationFrame (GPU-friendly, no layout thrash). Used by the
 * dashboard KPI donuts so figures tick up on mount instead of snapping.
 *
 * Respects prefers-reduced-motion: when reduced motion is requested (or
 * duration ≤ 0) it returns the final target immediately, so the numbers
 * and the derived donut arc render at their final value with no motion.
 *
 * VISUAL-ONLY: the value converges exactly on `target`, so any consumer
 * reads the same final number it would have shown without the hook.
 */

import { useEffect, useRef, useState } from 'react';

export function useCountUp(target: number, durationMs = 1200): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);
  const startRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || durationMs <= 0 || !Number.isFinite(target)) {
      setValue(target);
      return;
    }

    startRef.current = undefined;

    const tick = (ts: number): void => {
      if (startRef.current === undefined) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(target * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target); // land exactly on target
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}
